import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {CalendarRes, DocumentRes} from '../models/calendar/calendarRes';
import {CalendarStatsData} from '../models/calendarStats/calendarStatsReq';
import {OrderRes} from '../models/order/orderRes';
import {InitRes} from '../models/init/initRes';
import {GiftRes} from '../models/gift/giftRes';
import {ContactRes} from '../models/contact/contactRes';
import {DatePipe} from '@angular/common';

const CACHE_KEY = '_cached_';

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private memory = new Map<string, { data: any; validUntil: number }>();

  constructor(private datePipe: DatePipe) {}

  initStorage(): void {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_KEY)) {
        try {
          const value = JSON.parse(localStorage.getItem(key));
          this.memory.set(key, value);
        } catch {}
      }
    }
  }

  cacheRequests(url: string, data: any, timeToLive?: number): Observable<any> {
    const validUntil = Date.now() + (timeToLive ?? 25) * 1000;
    const key = `${CACHE_KEY}${url}`;
    const entry = {validUntil, data};
    this.memory.set(key, entry);
    try { localStorage.setItem(key, JSON.stringify(entry)); } catch {}
    return of(entry);
  }

  getCachedRequest(url: string): Observable<any> {
    const key = `${CACHE_KEY}${url}`;
    return of(this.memory.get(key) ?? null);
  }

  clearCacheData(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(CACHE_KEY)) { keysToRemove.push(k); }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    this.memory.clear();
  }

  invalidateCacheEntry(url: string): Observable<any> {
    const key = `${CACHE_KEY}${url}`;
    this.memory.delete(key);
    localStorage.removeItem(key);
    return of(null);
  }

  private getEntry(url: string): { data: any; validUntil: number } | null {
    return this.memory.get(`${CACHE_KEY}${url}`) ?? null;
  }

  cacheSubjectData(url: string, subjectData: InitRes): Observable<any> {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars = subjectData.calendars;
    entry.data.orders = subjectData.orders;
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateCachedCalendarStats(url: string, newCalendarStat: CalendarStatsData, calendar: CalendarRes, _connected: boolean) {
    calendar.calendar_contacts = newCalendarStat.calendar_contacts.toString();
    calendar.calendar_pairs = newCalendarStat.calendar_pairs.toString();
    calendar.calendar_sms = newCalendarStat.calendar_sms.toString();
    calendar.calendar_guests = newCalendarStat.calendar_guests.toString();

    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(oldCalendar => {
      if (Number(oldCalendar?.id) === Number(calendar?.id)) { return calendar; }
      return oldCalendar;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateCachedCalendarGifts(url: string, newCalendarGifts: GiftRes[], calendar: CalendarRes, _connected: boolean) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(oldCalendar => {
      if (Number(oldCalendar?.id) === Number(calendar?.id)) {
        oldCalendar.gifts = newCalendarGifts;
      }
      return oldCalendar;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateCachedContact(url: string, newContact: ContactRes, calendar: CalendarRes, recommendation: boolean = false, _connected: boolean) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    if (!newContact.documents) { newContact.documents = []; }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map((oldCalendar: CalendarRes) => {
      if (Number(oldCalendar?.id) === Number(calendar?.id)) {
        if (!recommendation) {
          const idx = oldCalendar.contacts.findIndex(c => Number(c.id) === Number(newContact.id));
          if (idx === -1) { oldCalendar.contacts.push(newContact); }
          else { oldCalendar.contacts[idx] = newContact; }
        } else {
          const idx = oldCalendar.recommendations.findIndex(r => Number(r.id) === Number(newContact.id));
          if (idx === -1) { oldCalendar.recommendations.push(newContact); }
          else { oldCalendar.recommendations[idx] = newContact; }
        }
      }
      return oldCalendar;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60).pipe(
      switchMap(() => of(newContact))
    );
  }

  deleteCachedContact(url: string, deletedContact: ContactRes, calendar: CalendarRes, recommendation: boolean = false) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(calendarOld => {
      if (Number(calendarOld?.id) === Number(calendar?.id)) {
        if (!recommendation) {
          const idx = calendarOld.contacts.findIndex(c => Number(c.id) === Number(deletedContact.id));
          if (idx !== -1) { calendarOld.contacts.splice(idx, 1); }
        } else {
          const idx = calendarOld.recommendations.findIndex(r => Number(r.id) === Number(deletedContact.id));
          if (idx !== -1) { calendarOld.recommendations.splice(idx, 1); }
        }
      }
      return calendarOld;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  deleteCachedOrder(url: string, deletedOrder: OrderRes, calendar: CalendarRes) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(calendarOld => {
      if (Number(calendarOld?.id) === Number(calendar?.id)) {
        const contact = calendarOld.contacts.find(c => Number(c.id) === Number(deletedOrder.contact_id));
        if (contact) {
          if (!contact.orders) { contact.orders = []; }
          const idx = contact.orders.findIndex(o => Number(o.id) === Number(deletedOrder.id));
          if (idx !== -1) { contact.orders.splice(idx, 1); }
        }
        const idxData = entry.data.orders.findIndex(o => Number(o.id) === Number(deletedOrder.id));
        if (idxData !== -1) { entry.data.orders.splice(idxData, 1); }
        return calendarOld;
      }
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateCachedOrder(url: string, newOrder: OrderRes, calendar: CalendarRes, _connected: boolean) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(calendarOld => {
      if (Number(calendarOld?.id) === Number(calendar?.id)) {
        const contact = calendarOld.contacts.find(c => Number(c.id) === Number(newOrder.contact_id));
        if (!contact) { return calendarOld; }
        if (!contact.orders) { contact.orders = []; }
        const idx = contact.orders.findIndex(o => Number(o.id) === Number(newOrder.id));
        if (idx === -1) { contact.orders.push(newOrder); }
        else { contact.orders[idx] = newOrder; }
        const cidx = calendarOld.contacts.findIndex(c => Number(c.id) === Number(contact.id));
        calendarOld.contacts[cidx] = contact;
        calendarOld.calendar_state = 'S070 - Ukážka úspešná';
        calendarOld.calendar_state_id = 19334;
        const oidx = entry.data.orders.findIndex(o => Number(o.id) === Number(newOrder.id));
        if (oidx !== -1) { entry.data.orders[oidx] = newOrder; }
        else { entry.data.orders.push(newOrder); }
        return calendarOld;
      }
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateCachedOrderDocumentNotSavingToOne(url: string, orderId: number, contactId: number, newDocument: DocumentRes, oldFileId: number, calendar: CalendarRes, _connected: boolean) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    const orderIdx = entry.data.orders.findIndex(o => Number(o.id) === Number(orderId));
    if (orderIdx !== -1) {
      if (!entry.data.orders[orderIdx].documents) { entry.data.orders[orderIdx].documents = []; }
      const docIdx = entry.data.orders[orderIdx].documents.findIndex(d => d.id === oldFileId);
      if (docIdx !== -1) { entry.data.orders[orderIdx].documents[docIdx].id = newDocument.id; }
      else { entry.data.orders[orderIdx].documents.push(newDocument); }
    }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(calendarOld => {
      if (Number(calendarOld?.id) === Number(calendar?.id)) {
        const contact = calendarOld.contacts.find(c => Number(c.id) === Number(contactId));
        if (!contact) { return calendarOld; }
        if (!contact.orders) { contact.orders = []; }
        const foundIndex = contact.orders.findIndex(o => Number(o.id) === Number(orderId));
        if (foundIndex !== -1) {
          if (!contact.orders[foundIndex].documents) { contact.orders[foundIndex].documents = []; }
          const docIdx = contact.orders[foundIndex].documents.findIndex(d => d.id === oldFileId);
          if (docIdx !== -1) { contact.orders[foundIndex].documents[docIdx].id = newDocument.id; }
          else { contact.orders[foundIndex].documents.push(newDocument); }
          const cidx = calendarOld.contacts.findIndex(c => Number(c.id) === Number(contact.id));
          calendarOld.contacts[cidx] = contact;
        }
        return calendarOld;
      }
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateCachedOrderDocument(url: string, orderId: number, contactId: number, newDocument: DocumentRes, oldDocumentId: number, calendar: CalendarRes, _connected: boolean) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    const orderIdx = entry.data.orders.findIndex(o => Number(o.id) === Number(orderId));
    if (orderIdx !== -1) {
      if (!entry.data.orders[orderIdx].documents) { entry.data.orders[orderIdx].documents = []; }
      const docIdx = entry.data.orders[orderIdx].documents.findIndex(d => d.id === oldDocumentId);
      if (docIdx !== -1) { entry.data.orders[orderIdx].documents[docIdx] = newDocument; }
      else { entry.data.orders[orderIdx].documents.push(newDocument); }
    }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(calendarOld => {
      if (Number(calendarOld?.id) === Number(calendar?.id)) {
        const contact = calendarOld.contacts.find(c => Number(c.id) === Number(contactId));
        if (!contact) { return calendarOld; }
        if (!contact.orders) { contact.orders = []; }
        const foundIndex = contact.orders.findIndex(o => Number(o.id) === Number(orderId));
        if (foundIndex !== -1) {
          if (!contact.orders[foundIndex].documents) { contact.orders[foundIndex].documents = []; }
          const docIdx = contact.orders[foundIndex].documents.findIndex(d => d.id === oldDocumentId);
          if (docIdx !== -1) { contact.orders[foundIndex].documents[docIdx] = newDocument; }
          else { contact.orders[foundIndex].documents.push(newDocument); }
          const cidx = calendarOld.contacts.findIndex(c => Number(c.id) === Number(contact.id));
          calendarOld.contacts[cidx] = contact;
        }
        return calendarOld;
      }
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateCachedCalendarDocument(url: string, newDocument: DocumentRes, _oldFileId: number, calendar: CalendarRes, _connected: boolean) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(calendarA => {
      if (Number(calendarA?.id) === Number(calendar?.id)) {
        calendarA.documents.push(newDocument);
      }
      return calendarA;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateCachedContactDocument(url: string, contactId: number, newDocument: DocumentRes, _oldFileId: number, calendar: CalendarRes, _connected: boolean) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(oldCalendar => {
      if (Number(oldCalendar?.id) === Number(calendar?.id)) {
        const idx = oldCalendar.contacts.findIndex(c => Number(c.id) === Number(contactId));
        if (idx !== -1) {
          if (!oldCalendar.contacts[idx].documents) { oldCalendar.contacts[idx].documents = []; }
          oldCalendar.contacts[idx].documents.push(newDocument);
        }
      }
      return oldCalendar;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateCachedCalendarStatsState(url: string, newCalendar: CalendarRes, calendar: CalendarRes, _connected: boolean) {
    calendar.calendar_contacts = newCalendar.calendar_contacts.toString();
    calendar.calendar_pairs = newCalendar.calendar_pairs.toString();
    calendar.calendar_sms = newCalendar.calendar_sms.toString();
    calendar.calendar_guests = newCalendar.calendar_guests.toString();
    calendar.calendar_state = newCalendar.calendar_state.toString();
    calendar.calendar_state_id = newCalendar.calendar_state_id.toString();

    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(oldCalendar => {
      if (Number(oldCalendar?.id) === Number(calendar?.id)) { return calendar; }
      return oldCalendar;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateCachedCalendar(url: string, newCalendar: CalendarRes, _oldCalendar: CalendarRes, _connected: boolean) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    if (!entry.data.calendars[newCalendar.date]) {
      entry.data.calendars[newCalendar.date] = [];
    }
    entry.data.calendars[newCalendar.date].push(newCalendar);
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  cachePhilipiakClubCard(url: string, cardNumber: string, contactId: number, calendar: CalendarRes) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(oldCalendar => {
      if (Number(oldCalendar?.id) === Number(calendar?.id)) {
        const idx = oldCalendar.contacts.findIndex(c => Number(c.id) === Number(contactId));
        if (idx !== -1) { oldCalendar.contacts[idx].card2 = cardNumber; }
      }
      return oldCalendar;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60).pipe(
      switchMap(() => of(cardNumber))
    );
  }

  updateUnsyncedCalendarGifts(url: string, newGifts: GiftRes[], calendar: CalendarRes) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(c => {
      if (Number(c?.id) === Number(calendar?.id)) { c.gifts = newGifts; }
      return c;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateUnsyncedCalendarStats(url: string, calendar: CalendarRes) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(c => {
      if (Number(c?.id) === Number(calendar?.id)) {
        c.calendar_contacts = calendar.calendar_contacts.toString();
        c.calendar_pairs = calendar.calendar_pairs.toString();
        c.calendar_sms = calendar.calendar_sms.toString();
        c.calendar_guests = calendar.calendar_guests.toString();
      }
      return c;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  changeSyncStatusCalendar(url: string, calendar: CalendarRes) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(c => {
      if (Number(c?.id) === Number(calendar?.id)) { c.unsynced = false; }
      return c;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  changeSyncStateCalendar(url: string, calendar: CalendarRes) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map((c: CalendarRes) => {
      if (Number(c?.id) === Number(calendar?.id)) {
        c.calendar_state_id = calendar.calendar_state_id;
        c.calendar_state = calendar.calendar_state;
      }
      return c;
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }

  updateUnsyncedContact(url: string, newContact: ContactRes, oldContact: ContactRes, calendar: CalendarRes, recommendation: boolean = false) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map((oldCalendar: CalendarRes) => {
      if (Number(oldCalendar?.id) === Number(calendar?.id)) {
        newContact.documents = oldContact.documents;
        newContact.orders = oldContact.orders;
        newContact.orders.forEach(order => order.contact_id = newContact.id);
        if (!recommendation) {
          const idx = oldCalendar.contacts.findIndex(x => Number(x.id) === Number(oldContact.oldId));
          oldCalendar.recommendations.forEach(r => {
            if (r.recommender_id === oldContact.oldId) { r.recommender_id = newContact.id; }
          });
          if (oldCalendar.orders) {
            oldCalendar.orders.forEach(o => {
              if (o.contact_id === oldContact.oldId) { o.contact_id = newContact.id; }
            });
          }
          oldCalendar.contacts[idx] = newContact;
        } else {
          const idx = oldCalendar.recommendations.findIndex(x => Number(x.id) === Number(oldContact.oldId));
          oldCalendar.recommendations[idx] = newContact;
        }
        return oldCalendar;
      }
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60).pipe(
      switchMap(() => of(newContact))
    );
  }

  updateUnsyncedOrder(url: string, newOrder: OrderRes, oldOrder: OrderRes, calendar: CalendarRes) {
    const entry = this.getEntry(url);
    if (!entry?.data) { return of(null); }
    entry.data.calendars[calendar.date] = entry.data.calendars[calendar.date].map(calendarOld => {
      if (Number(calendarOld?.id) === Number(calendar?.id)) {
        const contact = calendarOld.contacts.find(c => Number(c.id) === Number(newOrder.contact_id));
        if (!contact) { return calendarOld; }
        if (!contact.orders) { contact.orders = []; }
        if (!oldOrder) {
          contact.orders.push(newOrder);
        } else {
          newOrder.documents = oldOrder.documents;
          const idx = contact.orders.findIndex(o => Number(o.id) === Number(oldOrder.oldId));
          contact.orders[idx] = newOrder;
        }
        const cidx = calendarOld.contacts.findIndex(c => Number(c.id) === Number(contact.id));
        calendarOld.contacts[cidx] = contact;
        const oidx = entry.data.orders.findIndex(o => Number(o.id) === Number(oldOrder.oldId));
        if (oidx !== -1) { entry.data.orders[oidx] = newOrder; }
        else { entry.data.orders.push(newOrder); }
        return calendarOld;
      }
    });
    return this.cacheRequests(url, entry.data, 2 * 60 * 60);
  }
}
